const EntityStatus = require('@/lib/Entities/EntityStatus');
const EposPromoImage = require('@/lib/Devices/EposPromoImage');
const { container } = require('@/container');
const { populate } = require('@/functions/objects');
const {
    toBool,
    toInt,
    toNullableInt,
    toNullableString,
    toString,
} = require('@/functions/transform');

/**
 * @constructor
 *
 * @param {{}} data
 */
const Branch = function (data = {}) {
    this.address = null;
    /**
     * @type {Country|null}
     */
    this.country = null;
    this.customerBackground = null;
    this.customerWaitingBackground = null;
    /**
     * @type {EposPromoImage[]}
     */
    this.promoImages = null;
    this.ID = null;
    this.kitchenAmberMinutes = null;

    /**
     * Controls auto printing receipts on the kitchen screen.
     * Note: auto printing kitchen receipts in epos is controlled by the devices.autoPrintKitchenReceipts setting.
     *
     * @type {boolean}
     */
    this.kitchenAutoPrint = false;
    this.kitchenEnabled = false;
    this.kitchenOrderSoundUrl = null;
    this.kitchenReadySoundUrl = null;
    this.kitchenRedMinutes = null;
    this.kitchenRefRequired = false;
    this.kitchenTitle = null;
    this.language = null;
    this.languageOverrides = null;

    /**
     * URL to the Branch's logo. Used on the kitchen-customer screen.
     *
     * @type {?string}
     */
    this.logoUrl = null;

    this.name = '';
    this.themeID = null;

    this.status = EntityStatus.ACTIVE;

    /**
     * @type {?string}
     */
    this.timezoneName = null;

    /**
     * Print the cart/order reference on the customer's receipt?
     *
     * @type {boolean}
     */
    this.printStaffRef = false;

    this.pricingRulesActive = false;

    /**
     * @type {string|null}
     */
    this.email = '';

    /**
     * @type {string|null}
     */
    this.tel = null;

    /**
     * @type {string|null}
     */
    this.website = null;

    /**
     * Print the branch tel/email/website (true) or company info (false) on ProPoint receipts?
     *
     * @type {boolean}
     */
    this.useBranchInfoOnProPointReceipt = false;

    populate(this, data);
};

Branch.prototype = {
    getHydrationMap() {
        return {
            address: toNullableString,
            customerBackground: {
                field: ['eposCustomerBackground', 'customerBackground'],
            },
            customerWaitingBackground: {
                field: ['eposCustomerWaitingBackground', 'customerWaitingBackground'],
            },
            email: toNullableString,
            promoImages: {
                field: ['epospromoimages', 'promoImages'],
                modelCollection: EposPromoImage,
            },
            ID: {
                field: ['branchID', 'ID'],
                transform: toInt,
            },
            kitchenAmberMinutes: toInt,
            kitchenAutoPrint: toBool,
            kitchenEnabled: toBool,
            kitchenOrderSoundUrl: toNullableString,
            kitchenReadySoundUrl: toNullableString,
            kitchenRedMinutes: toInt,
            kitchenRefRequired: toBool,
            kitchenTitle: toString,
            language: {
                field: function (data) {
                    if (data.hasOwnProperty('language')) {
                        // Already hydrated
                        return data.language;
                    }
                    if (data.hasOwnProperty('languages')) {
                        return data.languages.code;
                    }
                    return null;
                },
            },
            languageOverrides: {},
            logoUrl: {},
            name: {},
            pricingRulesActive: toBool,
            printStaffRef: toBool,
            status: toString,
            tel: toNullableString,
            timezoneName: {
                field(data) {
                    if (data.timezoneName) {
                        return data.timezoneName;
                    }
                    if (data.timezones && data.timezones.name) {
                        // Extract attached 'timezones.name' to just a timezoneName property.
                        return data.timezones.name;
                    }
                    return null;
                },
            },
            themeID: toNullableInt,
            useBranchInfoOnProPointReceipt: toBool,
            website: toNullableString,
        };
    },

    /**
     * @param {object} data
     */
    afterHydration(data) {
        const countryManager = container.countryManager;

        if (countryManager) {
            this.country = countryManager.findByData(data);
        }

        // TODO: PROP-434 pull this value from the API.
        this.useBranchInfoOnProPointReceipt = [
            // Company: The Royal Mint Experience
            // Branch: 886 Shop
            // https://digitickets.atlassian.net/browse/PROP-431
            2821,

            // Company: The Beatles Story
            // Branch: The Beatles Story
            // https://digitickets.atlassian.net/browse/PROP-1213
            3214,
        ].indexOf(this.ID) !== -1;
    },

    getID() {
        return this.ID;
    },

    isSelectable() {
        return this.status !== EntityStatus.ARCHIVED && this.status !== EntityStatus.DELETED;
    },
};

module.exports = Branch;
